import React, { useState } from 'react';
import {
  Search, ChevronRight, ChevronLeft, Check, X, Clock,
  Download, Eye, Mail, Calendar, Briefcase, Phone,
  SlidersHorizontal, MoreHorizontal
} from 'lucide-react';

const APP = {
  bg:          '#f1f5f9',
  card:        '#ffffff',
  border:      '#e2e8f0',
  borderLight: '#f1f5f9',
  primary:     '#6366f1',
  primaryBg:   '#eef2ff',
  purple:      '#7c3aed',
  purpleBg:    '#ede9fe',
  green:       '#10b981',
  greenBg:     '#d1fae5',
  amber:       '#f59e0b',
  amberBg:     '#fef3c7',
  red:         '#ef4444',
  redBg:       '#fee2e2',
  sky:         '#0ea5e9',
  skyBg:       '#e0f2fe',
  text:        '#0f172a',
  textMd:      '#334155',
  textSm:      '#64748b',
  textXs:      '#94a3b8',
};

const STATUS_META = {
  pending:  { bg: APP.amberBg, color: '#92400e', Icon: Clock  },
  accepted: { bg: APP.greenBg, color: '#065f46', Icon: Check  },
  rejected: { bg: APP.redBg,   color: '#991b1b', Icon: X      },
};

const StatusBadge = ({ status }) => {
  const key = status?.toLowerCase();
  const meta = STATUS_META[key] || { bg: '#f1f5f9', color: APP.textSm, Icon: null };
  const Icon = meta.Icon;
  return (
    <span style={{ backgroundColor: meta.bg, color: meta.color }}
      className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium whitespace-nowrap">
      {Icon && <Icon size={10} />}
      {status?.charAt(0).toUpperCase() + status?.slice(1)}
    </span>
  );
};

const Avatar = ({ name }) => {
  const palette = [
    [APP.primaryBg, APP.primary],
    [APP.purpleBg,  APP.purple],
    [APP.greenBg,   APP.green],
    [APP.skyBg,     APP.sky],
    [APP.amberBg,   APP.amber],
  ];
  const [bg, color] = palette[(name?.charCodeAt(0) || 0) % palette.length];
  return (
    <div style={{ backgroundColor: bg, color, width: 36, height: 36 }}
      className="rounded-full text-sm font-bold flex items-center justify-center flex-shrink-0">
      {name?.charAt(0).toUpperCase()}
    </div>
  );
};

const PER_PAGE = 8;

/* ── This component renders ONLY the inner content — no Navbar, no sidebar ── */
const ApplicationsContent = ({ dashboardData }) => {
  const applications = dashboardData?.recentApplications || [];

  const [search,       setSearch]       = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sortBy,       setSortBy]       = useState('date_desc');
  const [page,         setPage]         = useState(1);
  const [expandedId,   setExpandedId]   = useState(null);

  const counts = {
    all:      applications.length,
    pending:  applications.filter(a => a.status?.toLowerCase() === 'pending').length,
    accepted: applications.filter(a => a.status?.toLowerCase() === 'accepted').length,
    rejected: applications.filter(a => a.status?.toLowerCase() === 'rejected').length,
  };

  const filtered = applications
    .filter(a => {
      const q = search.toLowerCase();
      const matchSearch = !q ||
        a.candidateName?.toLowerCase().includes(q) ||
        a.jobTitle?.toLowerCase().includes(q);
      const matchStatus = statusFilter === 'all' || a.status?.toLowerCase() === statusFilter;
      return matchSearch && matchStatus;
    })
    .sort((a, b) => {
      if (sortBy === 'date_desc') return new Date(b.date) - new Date(a.date);
      if (sortBy === 'date_asc')  return new Date(a.date) - new Date(b.date);
      if (sortBy === 'name')      return (a.candidateName || '').localeCompare(b.candidateName || '');
      return 0;
    });

  const totalPages = Math.max(1, Math.ceil(filtered.length / PER_PAGE));
  const paginated  = filtered.slice((page - 1) * PER_PAGE, page * PER_PAGE);

  const TABS = [
    { key: 'all',      label: 'All'      },
    { key: 'pending',  label: 'Pending'  },
    { key: 'accepted', label: 'Accepted' },
    { key: 'rejected', label: 'Rejected' },
  ];

  return (
    <div>
      {/* ── Section header ── */}
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 style={{ color: APP.text }} className="text-base font-bold">Applications</h2>
          <p style={{ color: APP.textSm }} className="text-xs mt-0.5">
            {counts.all} total · {counts.pending} pending review
          </p>
        </div>
        <button style={{ backgroundColor: APP.card, borderColor: APP.border, color: APP.textMd }}
          className="inline-flex items-center gap-1.5 px-3 py-2 text-xs border rounded-lg hover:bg-slate-50 transition-colors">
          <Download size={13} /> Export CSV
        </button>
      </div>

      {/* ── Stat pills ── */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
        {[
          { label: 'Total',    value: counts.all,      color: APP.primary, bg: APP.primaryBg },
          { label: 'Pending',  value: counts.pending,  color: APP.amber,   bg: APP.amberBg   },
          { label: 'Accepted', value: counts.accepted, color: APP.green,   bg: APP.greenBg   },
          { label: 'Rejected', value: counts.rejected, color: APP.red,     bg: APP.redBg     },
        ].map(({ label, value, color, bg }) => (
          <div key={label}
            style={{ backgroundColor: APP.card, border: `1px solid ${APP.border}` }}
            className="rounded-2xl px-4 py-3 flex items-center justify-between">
            <div>
              <p style={{ color: APP.textSm }} className="text-xs mb-0.5">{label}</p>
              <p style={{ color: APP.text }} className="text-2xl font-bold">{value}</p>
            </div>
            <div style={{ backgroundColor: bg, width: 40, height: 40 }}
              className="rounded-full flex items-center justify-center flex-shrink-0">
              <span style={{ color, fontSize: 15, fontWeight: 700 }}>{value}</span>
            </div>
          </div>
        ))}
      </div>

      {/* ── Filter card ── */}
      <div style={{ backgroundColor: APP.card, border: `1px solid ${APP.border}` }}
        className="rounded-2xl mb-4">

        {/* Tabs */}
        <div style={{ borderBottom: `1px solid ${APP.border}` }}
          className="flex items-center gap-1 px-4 pt-3 overflow-x-auto">
          {TABS.map(({ key, label }) => (
            <button key={key}
              onClick={() => { setStatusFilter(key); setPage(1); }}
              style={statusFilter === key
                ? { color: APP.primary, borderBottom: `2px solid ${APP.primary}` }
                : { color: APP.textSm,  borderBottom: '2px solid transparent' }}
              className="flex items-center gap-1.5 px-3 pb-2.5 text-sm font-medium whitespace-nowrap transition-colors">
              {label}
              <span style={{
                backgroundColor: statusFilter === key ? APP.primaryBg : APP.bg,
                color: statusFilter === key ? APP.primary : APP.textSm,
              }} className="text-[10px] font-bold px-1.5 py-0.5 rounded-full">
                {counts[key]}
              </span>
            </button>
          ))}
        </div>

        {/* Search + sort */}
        <div className="flex items-center gap-3 px-4 py-3 flex-wrap">
          <div className="relative flex-1 min-w-[180px]">
            <Search size={13} style={{ color: APP.textXs }}
              className="absolute left-3 top-1/2 -translate-y-1/2" />
            <input type="text" value={search}
              onChange={e => { setSearch(e.target.value); setPage(1); }}
              placeholder="Search by name or job title…"
              style={{ backgroundColor: APP.bg, borderColor: APP.border, color: APP.text }}
              className="w-full pl-8 pr-4 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-400" />
          </div>
          <div className="flex items-center gap-2">
            <SlidersHorizontal size={13} style={{ color: APP.textSm }} />
            <select value={sortBy} onChange={e => setSortBy(e.target.value)}
              style={{ backgroundColor: APP.bg, borderColor: APP.border, color: APP.textMd }}
              className="text-sm border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-400">
              <option value="date_desc">Newest first</option>
              <option value="date_asc">Oldest first</option>
              <option value="name">Name A–Z</option>
            </select>
          </div>
        </div>
      </div>

      {/* ── Table ── */}
      <div style={{ backgroundColor: APP.card, border: `1px solid ${APP.border}` }}
        className="rounded-2xl overflow-hidden mb-4">

        {/* Header */}
        <div className="grid gap-3 px-5 py-3 text-xs font-semibold uppercase tracking-wide"
          style={{
            gridTemplateColumns: '2fr 2fr 1.2fr 1.2fr 1fr',
            color: APP.textXs,
            borderBottom: `1px solid ${APP.border}`,
            backgroundColor: APP.bg,
          }}>
          <div>Candidate</div>
          <div>Position</div>
          <div>Applied</div>
          <div>Status</div>
          <div className="text-right">Actions</div>
        </div>

        {/* Empty state */}
        {paginated.length === 0 && (
          <div className="text-center py-14">
            <div style={{ backgroundColor: APP.primaryBg }}
              className="w-10 h-10 rounded-full flex items-center justify-center mx-auto mb-3">
              <Search size={18} style={{ color: APP.primary }} />
            </div>
            <p style={{ color: APP.text }} className="text-sm font-medium mb-1">No applications found</p>
            <p style={{ color: APP.textSm }} className="text-xs">Try adjusting your search or filters</p>
          </div>
        )}

        {/* Rows */}
        {paginated.map((app, idx) => (
          <React.Fragment key={app.id || idx}>
            <div
              className="grid gap-3 items-center px-5 py-3.5 hover:bg-slate-50 transition-colors cursor-pointer"
              style={{
                gridTemplateColumns: '2fr 2fr 1.2fr 1.2fr 1fr',
                borderBottom: expandedId === (app.id || idx)
                  ? `1px solid ${APP.border}`
                  : idx < paginated.length - 1
                    ? `1px solid ${APP.borderLight}`
                    : 'none',
              }}
              onClick={() => setExpandedId(expandedId === (app.id || idx) ? null : (app.id || idx))}>

              {/* Candidate */}
              <div className="flex items-center gap-2.5 min-w-0">
                <Avatar name={app.candidateName} />
                <div className="min-w-0">
                  <p style={{ color: APP.text }} className="text-sm font-medium truncate">{app.candidateName}</p>
                  <p style={{ color: APP.textSm }} className="text-[11px] truncate">{app.email || '—'}</p>
                </div>
              </div>

              {/* Position */}
              <div className="min-w-0">
                <p style={{ color: APP.textMd }} className="text-sm truncate">{app.jobTitle}</p>
              </div>

              {/* Date */}
              <div>
                <p style={{ color: APP.textSm }} className="text-xs">
                  {app.date
                    ? new Date(app.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
                    : '—'}
                </p>
              </div>

              {/* Status */}
              <div><StatusBadge status={app.status} /></div>

              {/* Actions */}
              <div className="flex items-center justify-end gap-1">
                <button title="View"
                  style={{ color: APP.textSm }}
                  className="p-1.5 rounded-lg hover:bg-slate-100 transition-colors"
                  onClick={e => e.stopPropagation()}>
                  <Eye size={14} />
                </button>
                <button title="Email"
                  style={{ color: APP.textSm }}
                  className="p-1.5 rounded-lg hover:bg-slate-100 transition-colors"
                  onClick={e => e.stopPropagation()}>
                  <Mail size={14} />
                </button>
                <button title="More"
                  style={{ color: APP.textSm }}
                  className="p-1.5 rounded-lg hover:bg-slate-100 transition-colors"
                  onClick={e => e.stopPropagation()}>
                  <MoreHorizontal size={14} />
                </button>
              </div>
            </div>

            {/* Expanded detail */}
            {expandedId === (app.id || idx) && (
              <div style={{ backgroundColor: APP.primaryBg, borderBottom: `1px solid ${APP.border}` }}
                className="px-5 py-4"
                onClick={e => e.stopPropagation()}>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-3">
                  {[
                    { Icon: Briefcase, label: 'Position', value: app.jobTitle },
                    { Icon: Calendar,  label: 'Applied',  value: app.date ? new Date(app.date).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }) : '—' },
                    { Icon: Mail,      label: 'Email',    value: app.email || '—' },
                    { Icon: Phone,     label: 'Phone',    value: app.phone || '—' },
                  ].map(({ Icon, label, value }) => (
                    <div key={label}>
                      <p style={{ color: APP.textXs }} className="text-[10px] font-semibold uppercase tracking-wide mb-1 flex items-center gap-1">
                        <Icon size={10} />{label}
                      </p>
                      <p style={{ color: APP.textMd }} className="text-xs font-medium truncate">{value}</p>
                    </div>
                  ))}
                </div>
                <div className="flex items-center gap-2">
                  {['accepted', 'rejected', 'pending'].map(s => {
                    const m = STATUS_META[s];
                    const active = app.status?.toLowerCase() === s;
                    return (
                      <button key={s}
                        style={active
                          ? { backgroundColor: m.bg, color: m.color, border: `1px solid ${m.color}40` }
                          : { backgroundColor: APP.card, color: APP.textSm, border: `1px solid ${APP.border}` }}
                        className="text-xs font-medium px-3 py-1.5 rounded-lg capitalize hover:opacity-80 transition-opacity">
                        {s.charAt(0).toUpperCase() + s.slice(1)}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </React.Fragment>
        ))}
      </div>

      {/* ── Pagination ── */}
      {filtered.length > PER_PAGE && (
        <div className="flex items-center justify-between">
          <p style={{ color: APP.textSm }} className="text-xs">
            Showing {Math.min((page - 1) * PER_PAGE + 1, filtered.length)}–{Math.min(page * PER_PAGE, filtered.length)} of {filtered.length}
          </p>
          <div className="flex items-center gap-1">
            <button disabled={page === 1} onClick={() => setPage(p => p - 1)}
              style={{ backgroundColor: APP.card, borderColor: APP.border, color: APP.textMd }}
              className="p-2 border rounded-lg disabled:opacity-40 hover:bg-slate-50 transition-colors">
              <ChevronLeft size={14} />
            </button>
            {Array.from({ length: totalPages }, (_, i) => i + 1).map(p => (
              <button key={p} onClick={() => setPage(p)}
                style={p === page
                  ? { backgroundColor: APP.primary, color: '#fff', borderColor: APP.primary }
                  : { backgroundColor: APP.card, color: APP.textMd, borderColor: APP.border }}
                className="w-8 h-8 border rounded-lg text-xs font-medium hover:opacity-80 transition-opacity">
                {p}
              </button>
            ))}
            <button disabled={page === totalPages} onClick={() => setPage(p => p + 1)}
              style={{ backgroundColor: APP.card, borderColor: APP.border, color: APP.textMd }}
              className="p-2 border rounded-lg disabled:opacity-40 hover:bg-slate-50 transition-colors">
              <ChevronRight size={14} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ApplicationsContent;
